// packages/ui-kit/src/brand/SpectrumBar.tsx
// C3F v18 — reference: design/system/components/brand/SpectrumBar.{jsx,prompt.md}
// The ONLY progress element allowed to wear the Spectrum signature: main
// progress of a screen, one per view. Repeated/secondary meters use accent.
import { type CSSProperties } from "react";

interface Props {
  value?: number;
  height?: number;
  showValue?: boolean;
  label?: string;
  /** Set when the bar sits on an always-Night surface (Card tone="night",
   *  StatCard dark): --fg-muted there measures 2.86:1, --sidebar-muted is the
   *  ink measured on dark. Containers that know their context propagate this. */
  dark?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function SpectrumBar({
  value = 100,
  height = 6,
  showValue = false,
  label,
  dark = false,
  className,
  style,
}: Props) {
  const v = Math.max(0, Math.min(100, value));
  return (
    // w-full, not auto: in a shrink-to-fit container an intrinsic-width flex
    // root collapsed to 0×6px — gradient present, nothing to paint.
    <div
      className={["flex w-full items-center gap-3", className].filter(Boolean).join(" ")}
      style={style}
    >
      <div className="flex-1 overflow-hidden rounded-pill bg-hover" style={{ height }}>
        <div
          className="h-full rounded-pill transition-[width] duration-slow"
          style={{
            width: `${v}%`,
            background: "var(--spectrum-8h)",
            // Keep the full gradient sweep visible regardless of fill width.
            backgroundSize: `${10000 / Math.max(v, 1)}% 100%`,
          }}
        />
      </div>
      {(showValue || label !== undefined) && (
        <span
          className={`px-mono whitespace-nowrap ${dark ? "text-sidebar-muted" : "text-fg-muted"}`}
        >
          {label ?? `${Math.round(v)}%`}
        </span>
      )}
    </div>
  );
}
